import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Mail, Phone, User, Edit3, Save, X, Lock } from 'lucide-react';
import { UserProfile } from '../Profile';
import { TUserSelfPayload } from '@/data/types';

interface ContactInfoCardProps {
  userProfile: UserProfile;
  updateProfile: (data: Partial<TUserSelfPayload>) => Promise<void>;
}

const ContactInfoCard: React.FC<ContactInfoCardProps> = ({ userProfile, updateProfile }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editData, setEditData] = useState({
    phone: userProfile.phone || '',
  });

  // Define contact items - only phone is editable, email and username are read-only
  const contactItems = [
    {
      icon: Mail,
      label: 'Email',
      value: userProfile.email,
      key: 'email',
      type: 'email',
      editable: false,
      description: 'Το email δεν μπορεί να αλλάξει',
    },
    {
      icon: Phone,
      label: 'Τηλέφωνο',
      value: userProfile.phone || 'Δεν έχει καθοριστεί',
      key: 'phone',
      type: 'tel',
      editable: true,
      description: 'Το τηλέφωνό σας για επικοινωνία',
    },
  ];

  const handleSave = async () => {
    setIsUpdating(true);
    try {
      await updateProfile({
        phone: editData.phone || null,
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update contact info:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancel = () => {
    setEditData({
      phone: userProfile.phone || '',
    });
    setIsEditing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="rounded-3xl border border-gray-100/50 bg-white/80 p-6 shadow-xl backdrop-blur-sm transition-all duration-300 hover:shadow-2xl"
    >
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg">
            <Phone className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Στοιχεία Επικοινωνίας</h2>
            <p className="text-sm text-gray-600">Πώς μπορούν να επικοινωνήσουν μαζί σας</p>
          </div>
        </div>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => (isEditing ? handleCancel() : setIsEditing(true))}
          disabled={isUpdating}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100/80 backdrop-blur-sm transition-all hover:bg-gray-200/80 disabled:opacity-50"
        >
          {isEditing ? <X className="h-5 w-5 text-gray-600" /> : <Edit3 className="h-5 w-5 text-gray-600" />}
        </motion.button>
      </div>

      {/* Contact Items */}
      <div className="space-y-4">
        {contactItems.map((item, index) => (
          <motion.div
            key={item.key}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1, duration: 0.3 }}
            className={`flex items-center gap-4 rounded-2xl border border-gray-100/50 p-4 transition-all duration-200 ${
              item.editable ? 'bg-gray-50/30 hover:bg-gray-50/60' : 'bg-gray-50/20'
            }`}
          >
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-xl shadow-sm ${
                item.editable ? 'bg-white' : 'bg-gray-100'
              }`}
            >
              <item.icon className={`h-5 w-5 ${item.editable ? 'text-gray-600' : 'text-gray-400'}`} />
            </div>

            <div className="flex-1">
              <div className="mb-1 flex items-center gap-2">
                <p className="text-sm font-medium text-gray-500">{item.label}</p>
                {!item.editable && <Lock className="h-3 w-3 text-gray-400" />}
              </div>
              <AnimatePresence mode="wait">
                {!isEditing || !item.editable ? (
                  <motion.p
                    key="display"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className={`font-semibold ${item.editable ? 'text-gray-900' : 'text-gray-600'}`}
                  >
                    {item.value}
                  </motion.p>
                ) : (
                  <motion.input
                    key="edit"
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    type={item.type}
                    value={editData[item.key as keyof typeof editData] || ''}
                    onChange={(e) => setEditData({ ...editData, [item.key]: e.target.value })}
                    className="w-full rounded-xl border border-gray-200/50 bg-white/90 px-3 py-2 text-sm font-semibold text-gray-900 backdrop-blur-sm transition-all focus:border-blue-300 focus:ring-2 focus:ring-blue-100/50 focus:outline-none"
                    placeholder={`Εισάγετε ${item.label.toLowerCase()}`}
                    disabled={isUpdating}
                  />
                )}
              </AnimatePresence>
              {!item.editable && <p className="mt-1 text-xs text-gray-400">{item.description}</p>}
            </div>
          </motion.div>
        ))}

        {/* Save/Cancel Buttons */}
        <AnimatePresence>
          {isEditing && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="flex gap-3 pt-4"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSave}
                disabled={isUpdating}
                className="flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 font-medium text-white shadow-lg transition-all hover:bg-blue-700 disabled:opacity-50"
              >
                {isUpdating ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Αποθήκευση Αλλαγών
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleCancel}
                disabled={isUpdating}
                className="flex items-center gap-2 rounded-xl bg-gray-200/80 px-6 py-3 font-medium text-gray-700 backdrop-blur-sm transition-all hover:bg-gray-300/80 disabled:opacity-50"
              >
                <X className="h-4 w-4" />
                Ακύρωση
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Info Note */}
      <div className="mt-6 rounded-xl border border-blue-100/50 bg-blue-50/50 p-4">
        <p className="text-sm text-blue-700">
          <span className="font-medium">Σημείωση:</span> Μόνο το τηλέφωνο μπορεί να επεξεργαστεί. Για αλλαγή του email ή
          του ονόματος χρήστη, επικοινωνήστε με τον διαχειριστή.
        </p>
      </div>
    </motion.div>
  );
};

export default ContactInfoCard;
